package com.macrofocus.eflux.ui.layer

import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.deckgl.toJSonArray
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.network.line.LineCoordinate
import com.macrofocus.eflux.ui.morphing.morphCoordinates
import deckgl.ScatterplotLayer
import js.objects.unsafeJso
import mui.material.styles.Theme
import org.locationtech.jts.geom.Point
import react.RefObject
import kotlin.js.Date

fun createLineCoordinatesLayer(
    dataLoader: DataLoader,
    lineCoordinates: Array<LineCoordinate>?,
    cartographic: Double,
    riskMask: Int,
    theme: Theme,
    isCartographicZoomed: Boolean,
    isCartographic: Boolean,
    probed: Coordinate?, selected: Array<Coordinate>,
    lastClickTime: RefObject<Double>
) : ScatterplotLayer<LineCoordinate> {
    val transparent = arrayOf(0, 0, 0, 1)
    val severe = arrayOf(139, 0, 0)
    val serious = arrayOf(255, 69, 0)
    val warning = arrayOf(255, 215, 0)
    val normal = arrayOf(0, 128, 0)
    val invalid = arrayOf(35, 135, 135)

    val array = if(lineCoordinates != null) lineCoordinates else emptyArray()

    val controller = dataLoader.controller
    val doubleClickDelay = 300 // ms

    return ScatterplotLayer<LineCoordinate>(unsafeJso {
        id = "line-coordinates-layer"

        visible = !isCartographic

        this.data = array
        opacity = 0.5 //if(isCartographicZoomed) 0.05 else 1.0
        asDynamic().positionFormat = "XY"
        getPosition = { d: LineCoordinate ->
            val physical = d.physicalGeometry.centroid
            val logical = d.logicalGeometry.centroid
            d.logicalGeometry.centroid.toJSonArray()

            val morphed: Array<Double>?
            if (physical != null) {
                morphed =
                    morphCoordinates(logical.coordinate!!, physical.coordinate!!, cartographic, 10).toJSonArray()
            } else {
                if (cartographic <= 10 / 2) {
                    morphed = logical.toJSonArray()
                } else {
                    morphed = emptyArray()
                }
            }
            morphed
        }
//        asDynamic().radiusUnits = "pixels"
        radiusScale = 30.0
        getRadius = { d: LineCoordinate ->
            val probability = d.probability
            val radius = when {
                probability == null -> 8.0
                probability >= 0.5 -> 16.0
                probability >= 0.1 -> 14.0
                probability >= 0.05 -> 12.0
                probability > 0.0 -> 10.0
                else -> 8.0
            }

//            if (d.physicalGeometry == probed?.physicalGeometry)
//                radius * 2
//            else if (selected.any { it.physicalGeometry == d.physicalGeometry })
//                radius * 2
//            else
                radius
        }
        asDynamic().getFillColor = { d: LineCoordinate ->
            val probability = d.probability
            if(d.risk_map and riskMask == 0) transparent
            else if (probability == null) transparent
            else if (probability >= 0.1) severe
            else if (probability >= 0.05) serious
            else if (probability > 0.0) warning
            else transparent
        }
        asDynamic().getLineWidth = { d: LineCoordinate ->
            val lineWidth = 1.0
            if (d.coordinateId == probed?.coordinateId)
                lineWidth * 2
            else if (selected.any { it.coordinateId == d.coordinateId })
                lineWidth * 2
            else
                lineWidth
        }
        asDynamic().getLineColor = { d: LineCoordinate ->
            if (d.coordinateId == probed?.coordinateId)
                arrayOf(253, 255, 180, 153)
            else if (selected.any { it.coordinateId == d.coordinateId })
                arrayOf(253, 255, 180, 127)
            else {
                val probability = d.probability
                if (d.risk_map and riskMask == 0) transparent
                else if (probability == null) transparent
                else if (probability >= 0.1) severe
                else if (probability >= 0.05) serious
                else if (probability > 0.0) warning
                else transparent
            }
        }
        asDynamic().lineWidthUnits = "pixels"
        asDynamic().lineWidthMinPixels = 2
        asDynamic().lineWidthMaxPixels = 4
        asDynamic().stroked = true
        pickable = true
        autoHighlight = true
        highlightColor = if(isCartographicZoomed) arrayOf(255, 255, 255, 31) else arrayOf(255, 255, 255, 63)
        updateTriggers = unsafeJso {
            this.asDynamic().getPosition = arrayOf(cartographic)
            this.asDynamic().opacity = arrayOf(isCartographicZoomed)
            this.asDynamic().getFillColor = arrayOf(isCartographicZoomed, riskMask)
            this.asDynamic().getLineColor = arrayOf(isCartographicZoomed, riskMask, probed, selected)
            this.asDynamic().highlightColor = arrayOf(isCartographicZoomed)
            this.asDynamic().getRadius = arrayOf(probed, selected)
            this.asDynamic().getLineWidth = arrayOf(probed, selected)
        }
        onHover = { info ->
            val o = info.`object`
            if (o != null) {
                if (o is LineCoordinate) {
                    val schema = (o as LineCoordinate)
                    println(schema.toString())
//                    dataLoader.controller.primaryLogicalBranchProbing.selected = logicalBranchIdOf(schema.logicalBranchId)
//                    dataLoader.controller.secondaryCoordinateProbing.selected = createCoordinate(schema)
                    controller.probeCoordinate(createCoordinate(schema))
                } else {
//                    dataLoader.controller.primaryLogicalBranchProbing.clearSelection()
//                    dataLoader.controller.secondaryCoordinateProbing.clearSelection()
                    controller.probeCoordinate(null)
                }
            } else {
//                dataLoader.controller.primaryLogicalBranchProbing.clearSelection()
//                dataLoader.controller.secondaryCoordinateProbing.clearSelection()
                controller.probeCoordinate(null)
            }
        }
        onClick = { info: dynamic ->
            val now = Date.now()
            val dbl = now - lastClickTime.current!! < doubleClickDelay
            lastClickTime.current = now

            val o = info.`object`
            if (o != null) {
                if (o is LineCoordinate) {
                    val schema = (o as LineCoordinate)
                    val coordinate = createCoordinate(schema)
                    if(dbl) {
                        dataLoader.physicalZoom(coordinate.physicalGeometry)
                    }
//                    dataLoader.controller.primaryLogicalBranchSelection.setSelectedElement(logicalBranchIdOf(schema.logicalBranchId))
//                    dataLoader.controller.secondaryCoordinateSelection.setSelectedElement(coordinate)
                    controller.selectCoordinate(createCoordinate(schema))
                } else {
//                    dataLoader.controller.primaryLogicalBranchSelection.clearSelection()
//                    dataLoader.controller.secondaryCoordinateSelection.clearSelection()
                    controller.selectCoordinate(null)
                }
            } else {
//                dataLoader.controller.primaryLogicalBranchSelection.clearSelection()
//                dataLoader.controller.secondaryCoordinateSelection.clearSelection()
                controller.selectCoordinate(null)
            }
        }

    })
}

private fun createCoordinate(o: LineCoordinate) : Coordinate {
    val physicalGeometry = o.physicalGeometry
    val logicalGeometry = o.logicalGeometry

    return Coordinate(
        o.coordinateId,
        physicalGeometry as? Point,
        logicalGeometry as? Point,
        o.seq,
        o.probability,
        o.risk_map,
        o.risk_list,
        o.logicalBranchIds, o.description, o.t_70m, o.wind_speed_70m, o.wind_gusts_70m_1h, o.precip_5min, o.precip_type_intensity_5min, o.forest_fire_warning, o.power_line_oscillation_24h, o.weather_code_1h, o.tstorm_warning_1h, o.air_density_70m, null
    )
}

